class ProductVariantSelect extends HTMLElement {
  abortController;

  connectedCallback() {
    this.handle = this.dataset.handle;
    this.sectionId = this.dataset.sectionId;
    this.productId = this.dataset.qsProductId;

    this.variants = JSON.parse(this.querySelector('[data-product-variants]').textContent);
    this.fieldsets = this.querySelectorAll('fieldset');
    this.optionNames = Array.from(this.fieldsets).map((fs) => fs.dataset.optionName);

    this.addEventListener('change', (event) => {
      const target = event.target.closest('[data-variant-id]');
      if (target && !this.abortController) this.onVariantChange({ target });
    });

    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;
      const target = event.target.closest('[data-variant-id]');
      if (!target || this.abortController) return;
      event.preventDefault();
      target.checked = true;
      this.onVariantChange({ target });
    });

    this.updateOptionStates(this.computeAvailability());
  }

  getSelections() {
    const selections = {};
    this.fieldsets.forEach((fieldset) => {
      const checked = fieldset.querySelector('[data-variant-id]:checked');
      selections[fieldset.dataset.optionName] = checked?.value ?? null;
    });
    return selections;
  }

  computeAvailability() {
    const selections = this.getSelections();
    const availability = {};

    this.optionNames.forEach((name, index) => {
      const filtered = this.variants.filter((variant) =>
        this.optionNames.every((_, otherIndex) => {
          if (otherIndex === index) return true;
          const selected = selections[this.optionNames[otherIndex]];
          return !selected || variant.options[otherIndex] === selected;
        })
      );

      availability[name] = new Set(
        filtered.filter((v) => v.available).map((v) => v.options[index])
      );
    });

    return availability;
  }

  updateOptionStates(availability) {
    this.querySelectorAll('[data-variant-id]').forEach((radio) => {
      const optionName = radio.closest('fieldset').dataset.optionName;
      const isAvailable = availability[optionName]?.has(radio.value) ?? false;

      radio.dataset.available = isAvailable;
      radio.setAttribute('aria-disabled', isAvailable ? 'false' : 'true');
    });
  }

  lookupVariantId() {
    const selections = this.getSelections();
    const values = this.optionNames.map((name) => selections[name]);
    const variant = this.variants.find((v) => v.options.every((opt, i) => opt === values[i]));
    return variant?.id;
  }

  allFieldsetsHaveSelectedOption() {
    return Array.from(this.fieldsets).every(
      (fieldset) =>
        fieldset.classList.contains('hidden') ||
        fieldset.querySelector('[data-variant-id]:checked') !== null
    );
  }

  async onVariantChange({ target }) {
    if (this.abortController) return;

    this.updateFieldset(target);
    this.updateOptionStates(this.computeAvailability());

    if (!this.allFieldsetsHaveSelectedOption()) return;

    const variantId = this.lookupVariantId();
    if (!variantId) return;

    this.abortController = new AbortController();

    try {
      const params = this.createParams(variantId);
      const response = await fetch(`/products/${this.handle}?${params}`, {
        signal: this.abortController.signal,
      });
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');

      publish(`variant:changed:${this.productId}`, { variantId, html });

      if (this.sectionId !== 'fetch-quick-shop') this.updateURL(variantId);
    } catch (error) {
      if (error.name === 'AbortError') return console.error('Variant change request was aborted.');
      console.error(error.message);
      window.toast.error(window.toast_translations.product.variant_error);
    } finally {
      this.abortController = null;
      document.querySelector(`#${target.id}`)?.focus();
    }
  }

  /**
   * Update URL
   * @param {String} value - URL value
   */
  updateURL(value) {
    const { searchParams } = new URL(window.location);
    searchParams.set('variant', value);

    // Replace the current history state with the new search params, without reloading the page
    history.replaceState(null, '', `${window.location.pathname}?${searchParams}`);
  }

  /**
   * Create the params.
   * @param {String} variantId - The variant ID.
   * @returns {String} The params.
   */
  createParams(variantId) {
    return [`section_id=${this.sectionId}`, `variant=${variantId}`].join('&');
  }

  /**
   * Update the fieldset.
   * @param {HTMLElement} target - The target element.
   */
  updateFieldset(target) {
    const fieldset = target.closest('fieldset');
    if (!fieldset) return;
    fieldset.querySelectorAll('[data-variant-id]').forEach((option) => {
      option.checked = option === target;
    });
  }
}

customElements.define('product-variant-select', ProductVariantSelect);
